import { useState, useMemo } from "react"
import { format, isSameDay, isWithinInterval } from "date-fns"
import { RiCalendarEventLine } from "@remixicon/react"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet"
import { cn } from "@workspace/web-ui/lib/utils"
import { Badge } from "@workspace/web-ui/components/badge"
import { isFuture, isPast } from "date-fns"

import type { CalendarEvent } from "~/features/dashboard/pages/calendar/components"
import { AppointmentDetailsModal } from "./event-modal"
import RefillCardModal from "../pages/refills/components/refill-card"
import { AppointmentDetailsFromData } from "./appointment-details-content"
import { useParams } from "@tanstack/react-router"

const formatTimeRange = (start: Date, end: Date) => {
  return `${format(start, "h:mm a")} - ${format(end, "h:mm a")}`
}

const getEventColor = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return isFuture(event.end) 
      ? "border border-[#019939] text-green-700" 
      : isPast(event.start) 
        ? "bg-[#E1FAEA] border border-green-200 text-green-800" 
        : "bg-[#019939] text-white"
  } else {
    return isFuture(event.end)
      ? "border border-[#3B82F6] text-blue-700"
      : isPast(event.start)
        ? "bg-[#EFF6FF] border border-blue-200 text-blue-800"
        : "bg-[#3B82F6] text-white"
  }
}

const getStatusBadgeStyles = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return isFuture(event.end) 
      ? "text-[#202020CC] bg-gray-300 border border-gray-300 hover:bg-gray-300" 
      : isPast(event.start) 
        ? "text-[#14AD4D] hover:bg-transparent bg-transparent border border-[#14AD4D]"
        : "bg-[#019939] hover:bg-[#019939] text-white"
  } else {
    return isFuture(event.end)
      ? "text-[#202020CC] bg-gray-300 border border-gray-300 hover:bg-gray-300"
      : isPast(event.start)
        ? "text-blue-600 hover:bg-transparent bg-transparent border border-blue-600"
        : "bg-blue-600 hover:bg-blue-600 text-white"
  }
}

const getEventStatus = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return isFuture(event.end) ? "upcoming" : isPast(event.start) ? "completed" : "ongoing"
  } else {
    if (event.status) return event.status
    return isFuture(event.end) ? "pending" : isPast(event.start) ? "completed" : "processing"
  }
}

const EventCard = ({ event, onClick }: { event: CalendarEvent, onClick: () => void }) => {
  return (
    <div
      onClick={onClick}
      className={cn(
        "p-4 rounded-md flex flex-col cursor-pointer",
        getEventColor(event)
      )}
    >
      {event.type === 'appointment' ? (
        <>
          <div className="flex items-center space-x-3 mb-1 w-full max-w-[800px]">
            <p className="font-medium">{event.clientName} Weight loss consulation</p>
            <Badge 
              className={cn(
                "rounded-full px-2 text-xs font-medium capitalize",
                getStatusBadgeStyles(event)
              )}
            >
              {getEventStatus(event)}
            </Badge>
          </div>
          <p className="text-sm">{formatTimeRange(event.start, event.end)}</p>
        </>
      ) : (
        <>
          <div className="flex items-center space-x-3 mb-1">
            <p className="font-medium">Refill: {event.medication}</p>
            <Badge 
              className={cn(
                "rounded-full px-2 text-xs font-medium capitalize",
                getStatusBadgeStyles(event)
              )}
            >
              {getEventStatus(event)}
            </Badge>
          </div>
          <p className="text-sm">{formatTimeRange(event.start, event.end)}</p>
          <p className="text-sm mt-1">Patient: {event.clientName}</p>
          <p className="text-sm">Quantity: {event.quantity}</p>
        </>
      )}
    </div>
  )
}

export function DayAppointment({ events, currentDate }: { events: CalendarEvent[], currentDate: Date }) {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const { program = "" } = useParams({ strict: false});

  // Get the events for the current date
  const getEventsForDate = (
    date: Date,
    events: CalendarEvent[]
  ): CalendarEvent[] => {
    return events.filter((event) => {
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)

      return (
        isSameDay(date, eventStart) ||
        isSameDay(date, eventEnd) ||
        isWithinInterval(date, { start: eventStart, end: eventEnd })
      )
    })
  }

  const eventsForDate = useMemo(() => getEventsForDate(currentDate, events), [currentDate, events])

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event)
  }

  const handleSheetClose = () => {
    setSelectedEvent(null)
  }

  return (
    <>
      <div className="flex flex-col gap-2 relative">
        <div className="flex flex-col space-y-1 border-y px-11 w-auto border-gray-300 py-3 mb-3">
          <p className="text-xs ml-2 font-medium text-[#464B55]">{format(currentDate, "EEE").toUpperCase()}</p>

          <span className="text-sm flex justify-center items-center text-white font-medium size-10 rounded-full bg-[#4272DD]">
            {format(currentDate, "d")}
          </span>
        </div>
        
        {eventsForDate.length === 0 ? (
          <div className="flex min-h-[70svh] flex-col items-center justify-center py-16 text-center">
            <RiCalendarEventLine size={32} className="text-muted-foreground/50 mb-2" />
            <h3 className="text-lg font-medium">No appointments</h3>
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-2">
            {eventsForDate.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => handleEventClick(event)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sheet for displaying event details */}
      <Sheet open={selectedEvent !== null} onOpenChange={(open) => !open && handleSheetClose()}>
        <SheetContent className="h-screen sm:max-w-[600px] w-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <SheetHeader>
            <SheetTitle>
              {selectedEvent && (
                selectedEvent.type === 'appointment' 
                  ? `${selectedEvent.clientName}'s Appointment` 
                  : `Refill: ${selectedEvent.medication}`
              )}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            {selectedEvent && (
              selectedEvent.type === 'appointment' ? (
                program === "urgent-care" ? (
                  <AppointmentDetailsFromData appointment={selectedEvent} />
                ) : (
                  <AppointmentDetailsModal
                    onClose={handleSheetClose}
                    data={selectedEvent}
                  />
                )
              ) : (
                <RefillCardModal
                  appointment={selectedEvent}
                  isSelected={true}
                  onOpen={() => {}}
                  onClose={handleSheetClose}
                />
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}